import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useDigestConfigGet, useDigestResultList } from "../hooks";
import type { DigestConfig, DigestResult } from "../types/digest";

import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import Loading from "../../../components/Loading";
import PageHeader from "../../../components/PageHeader";
import Page from "../../../components/Pages/Page";
import { FloatingChatWidget } from "../components/BKGAgent";

const DigestResultsList: React.FC = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();
  const { config_id } = useParams<{ config_id: string }>();

  const [config, setConfig] = useState<DigestConfig | null>(null);
  const [results, setResults] = useState<DigestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useDigestConfigGet();
  const fetchResults = useDigestResultList();

  useEffect(() => {
    if (!workspace?.id || !config_id) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [configData, resultsData] = await Promise.all([
          fetchConfig(workspace.id, config_id),
          fetchResults(workspace.id, config_id),
        ]);
        setConfig(configData);
        setResults(resultsData);
      } catch (err) {
        console.error("Error loading digest results:", err);
        setError("Failed to load digest results.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [workspace, config_id, fetchConfig, fetchResults]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "--";
    const utcStr = dateStr.endsWith("Z") ? dateStr : dateStr + "Z";
    return new Date(utcStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTriggerLabel = (generatedBy: string | null) => {
    if (!generatedBy) return "Unknown";
    return generatedBy.startsWith("manual:") ? "Manual" : "Scheduled";
  };

  if (!workspace?.id) {
    return (
      <Page>
        <Alert color="danger" variant="soft">
          Workspace not specified.
        </Alert>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="plain"
            color="neutral"
            size="sm"
            startDecorator={<ArrowBackIcon />}
            onClick={() =>
              navigate(`/workspace/${workspace.id}/bkg/catalyst-events/digests`)
            }
          >
            Back
          </Button>
          <Typography level="h1">
            {config?.name ?? "Digest"} -- History
          </Typography>
        </Stack>
      </PageHeader>

      <Stack spacing={2}>
        {error && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        {results.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Typography
                level="body-md"
                color="neutral"
                sx={{ textAlign: "center", py: 4 }}
              >
                No digests have been generated yet.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={1.5}>
            {results.map((result) => (
              <Card
                key={result.id}
                variant="outlined"
                sx={{
                  cursor: "pointer",
                  "&:hover": { borderColor: "primary.400" },
                  transition: "border-color 0.15s",
                }}
                onClick={() =>
                  navigate(
                    `/workspace/${workspace.id}/bkg/catalyst-events/digests/${config_id}/results/${result.id}`,
                  )
                }
              >
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack spacing={0.5}>
                      <Typography level="title-md">
                        {formatDate(result.generated_at)}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography level="body-sm" color="neutral">
                          {result.event_count} events analysed
                        </Typography>
                        {result.generation_duration_seconds != null && (
                          <Typography level="body-xs" color="neutral">
                            ({result.generation_duration_seconds.toFixed(1)}s)
                          </Typography>
                        )}
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="sm" variant="soft" color="neutral">
                        {getTriggerLabel(result.generated_by)}
                      </Chip>
                      {result.status === "error" ? (
                        <Chip
                          size="sm"
                          variant="soft"
                          color="danger"
                          startDecorator={
                            <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                          }
                        >
                          Error
                        </Chip>
                      ) : (
                        <Chip
                          size="sm"
                          variant="soft"
                          color="success"
                          startDecorator={
                            <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                          }
                        >
                          {result.items.length} items
                        </Chip>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
      <FloatingChatWidget workspaceId={workspace?.id ?? ""} />
    </Page>
  );
};

export default DigestResultsList;
